/**
 * HTML 표의 rowspan/colspan 을 펼쳐 화면 좌표 격자로 만든다.
 *
 * 서버가 주는 표 셀은 HTML 순서(행 번호, 그 행 안의 셀 순번)라 span 이 있으면 셀 순번이
 * 화면 열과 어긋난다. IXD 미리보기와 DSD 표가 같은 펼치기를 쓰도록 여기 한 벌만 둔다.
 */

export interface SpanGridSlot<T> {
  item: T;
  /** HTML 순서의 행 번호 */
  rowIndex: number;
  /** HTML 행 안의 셀 순번 */
  colIndex: number;
}

export interface SpanGrid<T> {
  /** `grid[화면 행][화면 열]` — span 이 덮는 칸에는 origin 셀의 슬롯이 반복된다. */
  grid: SpanGridSlot<T>[][];
  /** `spanGridKey(HTML 행, 셀 순번)` → origin 셀의 화면 좌표 */
  originIndex: Map<string, { row: number; col: number }>;
}

interface Spans {
  rowspan?: number | null;
  colspan?: number | null;
}

export const spanGridKey = (rowIndex: number, colIndex: number): string =>
  `${rowIndex},${colIndex}`;

const getSpan = (span: number | null | undefined): number =>
  Math.max(span ?? 1, 1);

export const expandSpanGrid = <T>(
  rows: readonly (readonly T[])[],
  spansOf: (item: T) => Spans,
): SpanGrid<T> => {
  const grid: SpanGridSlot<T>[][] = [];
  const originIndex = new Map<string, { row: number; col: number }>();

  rows.forEach((row, rowIndex) => {
    grid[rowIndex] ??= [];
    let visualColIndex = 0;

    row.forEach((item, colIndex) => {
      while (grid[rowIndex][visualColIndex]) {
        visualColIndex += 1;
      }

      const { rowspan, colspan } = spansOf(item);
      const rowCount = getSpan(rowspan);
      const colCount = getSpan(colspan);
      originIndex.set(spanGridKey(rowIndex, colIndex), {
        row: rowIndex,
        col: visualColIndex,
      });
      for (let rowOffset = 0; rowOffset < rowCount; rowOffset += 1) {
        const targetRowIndex = rowIndex + rowOffset;
        grid[targetRowIndex] ??= [];
        for (let colOffset = 0; colOffset < colCount; colOffset += 1) {
          grid[targetRowIndex][visualColIndex + colOffset] = {
            item,
            rowIndex,
            colIndex,
          };
        }
      }

      visualColIndex += colCount;
    });
  });

  return { grid, originIndex };
};
